import { AspectRatio, GcsImage } from "@zickko/shared";
import { useRef, useState } from "react";
import { getCssClassNameForAspectRatio } from "../utils/getCssClassNameForAspectRatio";
import { getImageUrl } from "../utils/getImageUrl";

/**
 * This component is used to dynamically render images based on the width of the container `div`.
 * This component first renders 100 pixels wide image, because it is super fast to load.
 * When the actual component is rendered on the screen, we will measure the actual width of the containing div and then fetch the image with the exact width.
 * We will always decide the aspect ratio of every image rendered on the screen before hand and not dynamically at run time.
 * Hence the aspect ratio has to be specified for every <Imgix > tag.
 *
 * Only in rare occasions where the aspect ratio cannot be specified, like rendering the logo in the Nav bar etc,
 * the aspectRatio will depend on the logo of the client so the height has to be specified instead.
 */
type Props = {
  image: GcsImage;
  aspectRatio: AspectRatio;
  onClick?: () => void;
};

const LOW_RESOLUTION_IMAGE_WIDTH = 100;

export const DynamicImage = (props: Props) => {
  //
  // const browser = useBrowser();
  const [width, setWidth] = useState(LOW_RESOLUTION_IMAGE_WIDTH);
  const imgRef = useRef<HTMLImageElement>(null);

  // if (!browser && !process.env.JEST_WORKER_ID) return null;

  const onLoad = () => {
    const element = imgRef.current;
    if (!element) return null;
    // the rendered width is less than the current width, so no need to load a fresh image
    if (element.width <= width) return null;
    // rendered width is greater than the loaded image's width, so load a bigger one
    if (width !== LOW_RESOLUTION_IMAGE_WIDTH) return null;
    // ceil to the next 100 so that we load images only of set dimensions like 200px, 300px etc,
    // this will help greatly in reducing the number of images that are cached
    setWidth(Math.ceil(element.width / 100) * 100);
  };

  const onError = () => console.log(`Error loading image ${props}`);

  const { image, aspectRatio } = props;
  if (!image) return null;
  const src = `${getImageUrl(image)}?fit=cover&width=${width}&aspectRatio=${props.aspectRatio}`;
  return (
    <div className="image imgix">
      <img
        className={getCssClassNameForAspectRatio(aspectRatio)}
        src={src}
        srcSet={`${src} 1x, ${src}&dpr=2 2x, ${src}&dpr=3 3x`}
        onLoad={onLoad}
        onError={onError}
        ref={imgRef}
        onClick={(e) => {
          e.preventDefault();
          if (props.onClick) props.onClick();
        }}
      ></img>
    </div>
  );
};
